'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';
import { partnerSocialIcon, partnerSocialLabel } from '@/components/market/identity/partner-social';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/app-tooltip';
import { Icon } from '@/components/ui/icon';
import { NewTwitterIcon } from '@/components/ui/icon/brands';
import { Agreement01IconSolid, CheckmarkCircle02Icon } from '@/components/ui/icon/glyphs';
import { TOAST_IDS } from '@/config/toast-ids';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
export interface PartnerSocialLink {
  platform: string;
  url: string;
  handle: string | null;
}

/** Verified partner social account: platform icon only, tooltip names the account, opens in a new tab. */
function PartnerSocialInline(t0) {
  const $ = _c(27);
  const {
    social,
    iconSize
  } = t0;
  const tPartner = useTranslations("profile.partner");
  let t1;
  if ($[0] !== social.handle || $[1] !== social.platform || $[2] !== tPartner) {
    const platform = partnerSocialLabel(social.platform);
    t1 = social.handle !== null ? tPartner("ownsAccount", {
      handle: social.handle,
      platform
    }) : tPartner("ownsPlatform", {
      platform
    });
    $[0] = social.handle;
    $[1] = social.platform;
    $[2] = tPartner;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const tooltip = t1;
  let t2;
  if ($[4] !== social.url) {
    t2 = function open() {
      window.open(social.url, "_blank", "noopener,noreferrer");
    };
    $[4] = social.url;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const open = t2;
  let t3;
  if ($[6] !== open) {
    t3 = function handleClick(event) {
      event.preventDefault();
      event.stopPropagation();
      open();
    };
    $[6] = open;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleClick = t3;
  let t4;
  if ($[8] !== open) {
    t4 = function handleKeyDown(event_0) {
      if (event_0.key !== "Enter") {
        return;
      }
      event_0.preventDefault();
      event_0.stopPropagation();
      open();
    };
    $[8] = open;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleKeyDown = t4;
  let t5;
  if ($[10] !== social.platform) {
    t5 = partnerSocialIcon(social.platform);
    $[10] = social.platform;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== iconSize) {
    t6 = cn(iconSize, "text-foreground-primary");
    $[12] = iconSize;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <Icon icon={t5} className={t6} aria-hidden="true" />;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== handleClick || $[18] !== handleKeyDown || $[19] !== t7 || $[20] !== tooltip) {
    t8 = <TooltipTrigger asChild={true}><span role="link" tabIndex={0} onClick={handleClick} onKeyDown={handleKeyDown} aria-label={tooltip} className="inline-flex cursor-pointer items-center align-middle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary">{t7}</span></TooltipTrigger>;
    $[17] = handleClick;
    $[18] = handleKeyDown;
    $[19] = t7;
    $[20] = tooltip;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== tooltip) {
    t9 = <TooltipContent><p>{tooltip}</p></TooltipContent>;
    $[22] = tooltip;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t8 || $[25] !== t9) {
    t10 = <TooltipProvider><Tooltip>{t8}{t9}</Tooltip></TooltipProvider>;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}
interface ConnectedProviderBadge {
  provider: string;
  username?: string | null | undefined;
}
interface UserNameDisplayProps {
  displayName: string | null;
  handle: string | null;
  /**
   * The canonical Morfi handle to copy. Defaults to `handle`; pass `null` when
   * the rendered handle is an X fallback rather than a Morfi handle.
   */
  copyableMorfiHandle?: string | null;
  /** Connected providers — shows verified badges between name and handle. */
  connectedProviders?: ConnectedProviderBadge[];
  /**
   * When false, omits inline provider badges (X, Google, Apple checkmark, etc.).
   * Names and handles still render; use on dense cards where icons duplicate
   * other chrome (e.g. a separate X link on the same surface).
   * @default true
   */
  showProviderBadges?: boolean;
  /** Partner creator — a handshake mark beside the name, before provider badges. */
  partner?: boolean;
  /** Partner's most prominent social account, shown as an icon + handle link after the mark. */
  partnerSocial?: PartnerSocialLink | null | undefined;
  /**
   * Fallback text when both displayName and handle are absent.
   * Defaults to the i18n "@anonymous_morfian" string. Override only for
   * context-specific fallbacks (e.g. email, "View profile", a wallet
   * address). Never pass "Anonymous" or "@anonymous_morfian" explicitly —
   * the component handles those internally.
   */
  fallback?: string;
  /** Size variant. */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Layout mode:
   * - `"inline"` (default): single line, name + handle truncate together. Use in
   *   dropdowns, cards, constrained-width rows — anywhere name + handle should stay on one line.
   * - `"stack"`: display name on the first row (with provider badges inline); `@handle` on the row below.
   */
  layout?: 'inline' | 'stack';
  /**
   * Classes for the root wrapper (`inline-flex` / `flex`). Use for layout,
   * width, and truncation of the whole cluster (`shrink-0`, `max-w-*`,
   * `truncate`, margins). Does not affect inner name/handle text tokens —
   * use `textClassName` for color/font overrides on the rendered name and @handle.
   */
  className?: string;
  /**
   * Merged **after** default name/handle styles on each visible text span so
   * utilities like `text-foreground-tertiary` override default colour pairs on the
   * name / `@handle` spans (tailwind-merge resolution).
   */
  textClassName?: string;
  /**
   * Merged onto the **handle** (`@…`) span only, after `textClassName`, so
   * callers can tune handle colour without affecting the display name.
   */
  handleClassName?: string;
  /**
   * Merged onto the **display name** span only, after `textClassName`, for
   * sizing or colour tweaks without changing `@handle` styles.
   */
  nameClassName?: string;
}
const NAME_SIZE_CLASSES = {
  sm: 'text-caption font-medium',
  md: 'text-body font-medium',
  lg: 'text-heading-md font-bold'
} as const;
const HANDLE_SIZE_CLASSES = {
  sm: 'text-caption font-normal',
  md: 'text-body-lg font-normal',
  lg: 'text-body-lg font-normal'
} as const;
const ICON_SIZE_CLASSES = {
  sm: 'size-3',
  md: 'size-3.5',
  lg: 'size-4'
} as const;

/** Only show social providers (X, Google, Apple) as badges. */
const BADGE_PROVIDERS: Set<string> = new Set(['x', 'twitter', 'google', 'apple']);
function getErrorName<T>(error: T): string {
  if (typeof error !== 'object' || error === null || !('name' in error)) return 'unknown';
  return typeof error.name === 'string' ? error.name : 'unknown';
}
function ProviderBadgeInline(t0) {
  const $ = _c(39);
  const {
    provider,
    iconSize,
    handle
  } = t0;
  const tBadge = useTranslations("profile.providerBadge");
  if (!BADGE_PROVIDERS.has(provider.provider)) {
    return null;
  }
  if (provider.provider === "x" || provider.provider === "twitter") {
    let t1;
    let tooltip;
    if ($[0] !== handle || $[1] !== iconSize || $[2] !== provider.username || $[3] !== tBadge) {
      const connectedXHandle = provider.username?.replace(/^@/, "") ?? null;
      const xHandle = connectedXHandle ?? handle;
      tooltip = xHandle !== null ? tBadge("xOwns", {
        handle: xHandle
      }) : tBadge("xConnected");
      const openXProfile = function openXProfile() {
        if (connectedXHandle === null || connectedXHandle.length === 0) {
          return;
        }
        window.open(`https://x.com/${encodeURIComponent(connectedXHandle)}`, "_blank", "noopener,noreferrer");
      };
      let t2;
      if ($[6] !== openXProfile) {
        t2 = function handleClick(event) {
          event.preventDefault();
          event.stopPropagation();
          openXProfile();
        };
        $[6] = openXProfile;
        $[7] = t2;
      } else {
        t2 = $[7];
      }
      const handleClick = t2;
      let t3;
      if ($[8] !== openXProfile) {
        t3 = function handleKeyDown(event_0) {
          if (event_0.key !== "Enter") {
            return;
          }
          event_0.preventDefault();
          event_0.stopPropagation();
          openXProfile();
        };
        $[8] = openXProfile;
        $[9] = t3;
      } else {
        t3 = $[9];
      }
      const handleKeyDown = t3;
      t1 = connectedXHandle !== null && connectedXHandle.length > 0 ? <span role="link" tabIndex={0} onClick={handleClick} onKeyDown={handleKeyDown} aria-label={tBadge("openXProfile", {
        handle: connectedXHandle
      })} className="inline-flex cursor-pointer items-center align-middle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary"><Icon icon={NewTwitterIcon} className={cn(iconSize, "text-foreground-primary")} aria-hidden="true" /></span> : <span className="inline-flex cursor-default items-center align-middle"><Icon icon={NewTwitterIcon} className={cn(iconSize, "text-foreground-primary")} aria-hidden="true" /></span>;
      $[0] = handle;
      $[1] = iconSize;
      $[2] = provider.username;
      $[3] = tBadge;
      $[4] = t1;
      $[5] = tooltip;
    } else {
      t1 = $[4];
      tooltip = $[5];
    }
    const trigger = t1;
    let t2;
    if ($[10] !== trigger) {
      t2 = <TooltipTrigger asChild={true}>{trigger}</TooltipTrigger>;
      $[10] = trigger;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    let t3;
    if ($[12] !== tooltip) {
      t3 = <TooltipContent><p>{tooltip}</p></TooltipContent>;
      $[12] = tooltip;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    let t4;
    if ($[14] !== t2 || $[15] !== t3) {
      t4 = <TooltipProvider><Tooltip>{t2}{t3}</Tooltip></TooltipProvider>;
      $[14] = t2;
      $[15] = t3;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    return t4;
  }
  if (provider.provider === "google") {
    let t1;
    if ($[17] !== iconSize) {
      t1 = cn(iconSize, "text-green-500");
      $[17] = iconSize;
      $[18] = t1;
    } else {
      t1 = $[18];
    }
    let t2;
    if ($[19] !== t1) {
      t2 = <TooltipTrigger asChild={true}><span className="inline-flex cursor-default items-center align-middle"><Icon icon={CheckmarkCircle02Icon} className={t1} aria-hidden="true" /></span></TooltipTrigger>;
      $[19] = t1;
      $[20] = t2;
    } else {
      t2 = $[20];
    }
    let t3;
    if ($[21] !== tBadge) {
      t3 = tBadge("googleVerified");
      $[21] = tBadge;
      $[22] = t3;
    } else {
      t3 = $[22];
    }
    let t4;
    if ($[23] !== t3) {
      t4 = <TooltipContent><p>{t3}</p></TooltipContent>;
      $[23] = t3;
      $[24] = t4;
    } else {
      t4 = $[24];
    }
    let t5;
    if ($[25] !== t2 || $[26] !== t4) {
      t5 = <TooltipProvider><Tooltip>{t2}{t4}</Tooltip></TooltipProvider>;
      $[25] = t2;
      $[26] = t4;
      $[27] = t5;
    } else {
      t5 = $[27];
    }
    return t5;
  }
  if (provider.provider === "apple") {
    const t1 = iconSize === "size-3" ? "text-micro" : "text-caption";
    let t2;
    if ($[28] !== t1) {
      t2 = cn("inline-flex cursor-default items-center font-bold text-foreground-secondary", t1);
      $[28] = t1;
      $[29] = t2;
    } else {
      t2 = $[29];
    }
    let t3;
    if ($[30] !== t2) {
      t3 = <TooltipTrigger asChild={true}><span className={t2} /></TooltipTrigger>;
      $[30] = t2;
      $[31] = t3;
    } else {
      t3 = $[31];
    }
    let t4;
    if ($[32] !== tBadge) {
      t4 = tBadge("appleVerified");
      $[32] = tBadge;
      $[33] = t4;
    } else {
      t4 = $[33];
    }
    let t5;
    if ($[34] !== t4) {
      t5 = <TooltipContent><p>{t4}</p></TooltipContent>;
      $[34] = t4;
      $[35] = t5;
    } else {
      t5 = $[35];
    }
    let t6;
    if ($[36] !== t3 || $[37] !== t5) {
      t6 = <TooltipProvider><Tooltip>{t3}{t5}</Tooltip></TooltipProvider>;
      $[36] = t3;
      $[37] = t5;
      $[38] = t6;
    } else {
      t6 = $[38];
    }
    return t6;
  }
  return null;
}
function HandleText(t0) {
  const $ = _c(15);
  const {
    handle,
    copyAriaLabel,
    onCopy,
    className
  } = t0;
  if (copyAriaLabel === null) {
    let t1;
    if ($[0] !== className || $[1] !== handle) {
      t1 = <span className={className}>@{handle}</span>;
      $[0] = className;
      $[1] = handle;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== onCopy) {
    t1 = function handleClick(event) {
      event.preventDefault();
      event.stopPropagation();
      onCopy();
    };
    $[3] = onCopy;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleClick = t1;
  let t2;
  if ($[5] !== onCopy) {
    t2 = function handleKeyDown(event_0) {
      if (event_0.key !== "Enter" && event_0.key !== " ") {
        return;
      }
      event_0.preventDefault();
      event_0.stopPropagation();
      onCopy();
    };
    $[5] = onCopy;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleKeyDown = t2;
  let t3;
  if ($[7] !== className) {
    t3 = cn(className, "cursor-copy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary");
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== copyAriaLabel || $[10] !== handle || $[11] !== handleClick || $[12] !== handleKeyDown || $[13] !== t3) {
    t4 = <span role="button" tabIndex={0} onClick={handleClick} onKeyDown={handleKeyDown} aria-label={copyAriaLabel} className={t3}>@{handle}</span>;
    $[9] = copyAriaLabel;
    $[10] = handle;
    $[11] = handleClick;
    $[12] = handleKeyDown;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}

/**
 * Centralized user name display: `displayName [provider badges] @handle`
 *
 * Used throughout the app for consistent user identification.
 * Renders inline (single line) like Twitter's verified name display.
 *
 * Handle-only mode: when `displayName` is null but `handle` is set, the
 * name slot is suppressed entirely and the handle keeps handle typography
 * (font-normal, `text-body-lg` at md/lg per tokens — see globals `@theme`).
 * It does not use bold name styling.
 *
 * **Colour:** `layout="inline"` — `@handle` is `text-foreground-primary`. `layout="stack"`
 * — `@handle` is `text-foreground-tertiary` (second-line emphasis). Override via `handleClassName`.
 */
export function UserNameDisplay(t0) {
  const $ = _c(82);
  const {
    displayName,
    handle,
    copyableMorfiHandle,
    connectedProviders,
    showProviderBadges: t1,
    partner: t2,
    partnerSocial: t3,
    fallback,
    size: t4,
    layout: t5,
    className,
    textClassName,
    handleClassName,
    nameClassName
  } = t0;
  const showProviderBadges = t1 === undefined ? true : t1;
  const partner = t2 === undefined ? false : t2;
  const partnerSocial = t3 === undefined ? null : t3;
  const size = t4 === undefined ? "md" : t4;
  const layout = t5 === undefined ? "inline" : t5;
  const t = useTranslations("profile");
  const tHandle = useTranslations("profile.handleInteraction");
  const tPartner = useTranslations("profile.partner");
  const nameClasses = NAME_SIZE_CLASSES[size];
  const handleClasses = HANDLE_SIZE_CLASSES[size];
  const iconSize = ICON_SIZE_CLASSES[size];
  let copyAriaLabel;
  let copyMorfiHandle;
  let effectiveHandle;
  let name;
  let showName;
  let t6;
  if ($[0] !== connectedProviders || $[1] !== copyableMorfiHandle || $[2] !== displayName || $[3] !== fallback || $[4] !== handle || $[5] !== iconSize || $[6] !== partner || $[7] !== partnerSocial || $[8] !== showProviderBadges || $[9] !== t || $[10] !== tHandle || $[11] !== tPartner) {
    const visibleProviders = showProviderBadges ? (connectedProviders ?? []).filter(_temp) : [];
    const isAnonymousDefault = displayName === null && handle === null && fallback === undefined;
    let t7;
    if ($[18] !== isAnonymousDefault || $[19] !== t) {
      t7 = isAnonymousDefault ? t("anonymousMorfian").replace(/^@/, "") : null;
      $[18] = isAnonymousDefault;
      $[19] = t;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    const anonymousHandle = t7;
    showName = displayName !== null || handle === null && !isAnonymousDefault;
    let t8;
    if ($[21] !== displayName || $[22] !== fallback || $[23] !== t) {
      t8 = displayName ?? fallback ?? t("anonymousMorfian");
      $[21] = displayName;
      $[22] = fallback;
      $[23] = t;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    name = t8;
    effectiveHandle = handle ?? anonymousHandle;
    const morfiHandle = copyableMorfiHandle === undefined ? handle : copyableMorfiHandle;
    let t9;
    if ($[25] !== morfiHandle) {
      t9 = morfiHandle !== null ? `@${morfiHandle.replace(/^@/, "")}` : null;
      $[25] = morfiHandle;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    const formattedMorfiHandle = t9;
    let t10;
    if ($[27] !== tHandle) {
      t10 = function handleCopyFailure(error) {
        logger.warn("profile.handle_copy_failed", {
          errorName: getErrorName(error)
        });
        showToast({
          id: TOAST_IDS.MORFI_HANDLE_COPY,
          message: tHandle("copyFailed"),
          variant: "error"
        });
      };
      $[27] = tHandle;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    const handleCopyFailure = t10;
    let t11;
    if ($[29] !== formattedMorfiHandle || $[30] !== handleCopyFailure || $[31] !== tHandle) {
      t11 = function copyMorfiHandle() {
        if (formattedMorfiHandle === null) {
          return;
        }
        if (navigator.clipboard === undefined) {
          handleCopyFailure({
            name: "ClipboardUnavailable"
          });
          return;
        }
        navigator.clipboard.writeText(formattedMorfiHandle).then(() => {
          showToast({
            id: TOAST_IDS.MORFI_HANDLE_COPY,
            message: tHandle("copied"),
            variant: "success"
          });
        }, handleCopyFailure);
      };
      $[29] = formattedMorfiHandle;
      $[30] = handleCopyFailure;
      $[31] = tHandle;
      $[32] = t11;
    } else {
      t11 = $[32];
    }
    copyMorfiHandle = t11;
    let t12;
    if ($[33] !== formattedMorfiHandle || $[34] !== tHandle) {
      t12 = formattedMorfiHandle !== null ? tHandle("copyAria", {
        handle: formattedMorfiHandle
      }) : null;
      $[33] = formattedMorfiHandle;
      $[34] = tHandle;
      $[35] = t12;
    } else {
      t12 = $[35];
    }
    copyAriaLabel = t12;
    t6 = partner || visibleProviders.length > 0 ? <span className="inline-flex shrink-0 items-center gap-1">{partner ? <Icon icon={Agreement01IconSolid} className={cn(iconSize, "text-product-partner-content")} aria-label={tPartner("badge")} /> : null}{partner && partnerSocial ? <PartnerSocialInline social={partnerSocial} iconSize={iconSize} /> : null}{visibleProviders.map(p_0 => <ProviderBadgeInline key={p_0.provider} provider={p_0} iconSize={iconSize} handle={handle} />)}</span> : null;
    $[0] = connectedProviders;
    $[1] = copyableMorfiHandle;
    $[2] = displayName;
    $[3] = fallback;
    $[4] = handle;
    $[5] = iconSize;
    $[6] = partner;
    $[7] = partnerSocial;
    $[8] = showProviderBadges;
    $[9] = t;
    $[10] = tHandle;
    $[11] = tPartner;
    $[12] = copyAriaLabel;
    $[13] = copyMorfiHandle;
    $[14] = effectiveHandle;
    $[15] = name;
    $[16] = showName;
    $[17] = t6;
  } else {
    copyAriaLabel = $[12];
    copyMorfiHandle = $[13];
    effectiveHandle = $[14];
    name = $[15];
    showName = $[16];
    t6 = $[17];
  }
  const badges = t6;
  let t7;
  if ($[36] !== badges || $[37] !== showName) {
    t7 = showName || badges !== null ? <span aria-hidden={true} className="shrink-0 select-none text-foreground-tertiary">{"\xB7"}</span> : null;
    $[36] = badges;
    $[37] = showName;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  const inlineHandleSeparator = t7;
  if (layout === "stack") {
    let t8;
    if ($[39] !== className) {
      t8 = cn("flex min-w-0 w-full flex-col items-start gap-y-0.5", className);
      $[39] = className;
      $[40] = t8;
    } else {
      t8 = $[40];
    }
    let t9;
    if ($[41] !== name || $[42] !== nameClassName || $[43] !== nameClasses || $[44] !== showName || $[45] !== textClassName) {
      t9 = showName && <span className={cn(nameClasses, "min-w-0 truncate text-foreground-primary", textClassName, nameClassName)}>{name}</span>;
      $[41] = name;
      $[42] = nameClassName;
      $[43] = nameClasses;
      $[44] = showName;
      $[45] = textClassName;
      $[46] = t9;
    } else {
      t9 = $[46];
    }
    let t10;
    if ($[47] !== badges || $[48] !== t9) {
      t10 = <span className="inline-flex w-fit max-w-full min-w-0 items-center gap-2">{t9}{badges}</span>;
      $[47] = badges;
      $[48] = t9;
      $[49] = t10;
    } else {
      t10 = $[49];
    }
    let t11;
    if ($[50] !== copyAriaLabel || $[51] !== copyMorfiHandle || $[52] !== effectiveHandle || $[53] !== handleClassName || $[54] !== handleClasses || $[55] !== textClassName) {
      t11 = effectiveHandle !== null && <HandleText handle={effectiveHandle} copyAriaLabel={copyAriaLabel} onCopy={copyMorfiHandle} className={cn(handleClasses, "min-w-0 max-w-full truncate text-foreground-tertiary", textClassName, handleClassName)} />;
      $[50] = copyAriaLabel;
      $[51] = copyMorfiHandle;
      $[52] = effectiveHandle;
      $[53] = handleClassName;
      $[54] = handleClasses;
      $[55] = textClassName;
      $[56] = t11;
    } else {
      t11 = $[56];
    }
    let t12;
    if ($[57] !== t10 || $[58] !== t11 || $[59] !== t8) {
      t12 = <span className={t8}>{t10}{t11}</span>;
      $[57] = t10;
      $[58] = t11;
      $[59] = t8;
      $[60] = t12;
    } else {
      t12 = $[60];
    }
    return t12;
  }
  let t8;
  if ($[61] !== className) {
    t8 = cn("inline-flex items-center gap-1 whitespace-nowrap", className);
    $[61] = className;
    $[62] = t8;
  } else {
    t8 = $[62];
  }
  let t9;
  if ($[63] !== name || $[64] !== nameClassName || $[65] !== nameClasses || $[66] !== showName || $[67] !== textClassName) {
    t9 = showName && <span className={cn(nameClasses, "text-foreground-primary", textClassName, nameClassName)}>{name}</span>;
    $[63] = name;
    $[64] = nameClassName;
    $[65] = nameClasses;
    $[66] = showName;
    $[67] = textClassName;
    $[68] = t9;
  } else {
    t9 = $[68];
  }
  let t10;
  if ($[69] !== copyAriaLabel || $[70] !== copyMorfiHandle || $[71] !== effectiveHandle || $[72] !== handleClassName || $[73] !== handleClasses || $[74] !== inlineHandleSeparator || $[75] !== textClassName) {
    t10 = effectiveHandle !== null && <>{inlineHandleSeparator}<HandleText handle={effectiveHandle} copyAriaLabel={copyAriaLabel} onCopy={copyMorfiHandle} className={cn(handleClasses, "text-foreground-primary", textClassName, handleClassName)} /></>;
    $[69] = copyAriaLabel;
    $[70] = copyMorfiHandle;
    $[71] = effectiveHandle;
    $[72] = handleClassName;
    $[73] = handleClasses;
    $[74] = inlineHandleSeparator;
    $[75] = textClassName;
    $[76] = t10;
  } else {
    t10 = $[76];
  }
  let t11;
  if ($[77] !== badges || $[78] !== t10 || $[79] !== t8 || $[80] !== t9) {
    t11 = <span className={t8}>{t9}{badges}{t10}</span>;
    $[77] = badges;
    $[78] = t10;
    $[79] = t8;
    $[80] = t9;
    $[81] = t11;
  } else {
    t11 = $[81];
  }
  return t11;
}
function _temp(p) {
  return BADGE_PROVIDERS.has(p.provider);
}